import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import StatusIndicator, {
  type StatusIndicatorProps,
} from '@cloudscape-design/components/status-indicator';
import {
  TenantStatus,
  deriveTenantDisplayStatus,
  type Subscription,
  type Tenant,
  TenantDisplayStatus,
} from '@etiquetachef/types';
import { formatDate } from '@/lib/format';
import { isCancellationScheduled } from '@/features/billing/scheduledCancellation';

// Cor/ícone semânticos do Cloudscape por status comercial. Alinhado ao
// `BillingCard` (success/warning/error/stopped/pending); COURTESY, DEMO e
// PARTNER (tipos comerciais sem cobrança nossa, #679 F7) usam `info` e
// os estados de carga `loading`, que o BillingCard não tem.
const INDICATOR_TYPE: Record<TenantDisplayStatus, StatusIndicatorProps.Type> = {
  ACTIVE: 'success',
  NO_SUBSCRIPTION: 'pending',
  NO_PLAN: 'pending',
  PAYMENT_PENDING: 'warning',
  SUSPENDED: 'error',
  CANCELLED: 'stopped',
  COURTESY: 'info',
  DEMO: 'info',
  PARTNER: 'info',
  CLOSED: 'stopped',
  // #1006 — tenant de teste: informativo, como demo; não é cliente.
  TEST: 'info',
};

type Props = {
  /** `id` decide se é tenant de teste (#1006). */
  tenant: { readonly id: string; readonly status: Tenant['status'] };
  subscription: Subscription | null;
  /**
   * `true` enquanto o doc `billing/subscription` ainda carrega. Só importa
   * quando o tenant é ACTIVE (os demais estados não dependem do billing): sem
   * isso, `subscription` chega `null` durante o load e o badge piscaria
   * "Sem plano ativo" para um tenant pago.
   */
  billingPending?: boolean;
  /**
   * `true` quando o doc `billing/subscription` existe mas falhou ao
   * carregar/parsear (`BillingDocResult.kind === 'ERROR'`). Distinto de
   * "não existe" (NOT_FOUND → legítimo "Sem plano ativo"): aqui o status
   * comercial é DESCONHECIDO. Sem isso, um tenant pago com doc ilegível
   * apareceria como "Sem plano ativo" (auditoria #100). Só importa para
   * tenant ACTIVE — os demais não dependem do billing.
   */
  billingError?: boolean;
};

/**
 * Badge do status comercial da empresa. Deriva o status combinando
 * `tenant.status` + billing (issue #100) — não confundir com `StatusBadge`,
 * que exibe `tenant.status` cru (estado operacional).
 *
 * Origem: #100 — commit ccc4418ce, 2026-06-28 13:16 +0200.
 *
 * Por quê: "Ativo" cru mentia para tenant sem plano ou com pagamento
 * pendente. A regra de derivação é `deriveTenantDisplayStatus`
 * (`@etiquetachef/types`), compartilhada com os outros clientes — não
 * duplicar aqui.
 *
 * Usado por: `TenantInfoCard` (topo do detalhe) e `TenantList` (coluna
 * "Assinatura" da lista de Empresas).
 *
 * Falhas conhecidas:
 *  - 2026-06-28 (#100): doc de billing ilegível aparecia como "Sem plano
 *    ativo" para tenant pago; por isso `billingError` e `billingPending`.
 *
 * Histórico:
 *  - 2026-10-13 17:45 +0200 — (#961) ACTIVE com cancelamento agendado mostra
 *    "Ativo — cancelamento agendado para <fim do período>" (pedido do owner).
 *  - 2026-10-19 18:04 +0200 — (#1006) tenant de teste (cadastro ou assinatura
 *    sandbox) mostra "Teste"; o badge passou a exigir `tenant.id`.
 */
export function TenantDisplayStatusBadge({
  tenant,
  subscription,
  billingPending = false,
  billingError = false,
}: Props): React.JSX.Element {
  const { t, i18n } = useTranslation();
  if (tenant.status === TenantStatus.ACTIVE) {
    if (billingPending) {
      return <StatusIndicator type="loading">{t('common.loading')}</StatusIndicator>;
    }
    if (billingError) {
      return (
        <StatusIndicator type="warning">{t('tenants.statusUnavailable')}</StatusIndicator>
      );
    }
  }
  const status = deriveTenantDisplayStatus(tenant, subscription);
  // #961 — ainda ativo, mas com fim marcado no gateway: a lista precisa dizer
  // quando o acesso acaba, senão "Ativo" parece cliente que continua. A data é
  // o `current_period_end` (até onde pagou). O `warning` destaca quem está saindo.
  if (status === TenantDisplayStatus.ACTIVE && subscription && isCancellationScheduled(subscription)) {
    return (
      <StatusIndicator type="warning">
        {t('tenants.activeCancelScheduled', {
          date: formatDate(subscription.current_period_end, i18n.language),
        })}
      </StatusIndicator>
    );
  }
  return (
    <StatusIndicator type={INDICATOR_TYPE[status]}>
      {t(`tenants.displayStatus.${status}`)}
    </StatusIndicator>
  );
}
